/**
 * 회원 상세 패널 — 탭 구성
 *
 *  회원 목록(마스터-디테일) 우측 패널과 /members/:userSeq 단독 페이지에서 공용.
 *  섹션을 탭으로 분리: 기본정보 / 알림설정 / 반려동물
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  membersApi,
  ticketsApi,
  type AdminPetInfoResponseDto,
  type GuardianGroupMemberDto,
  type TicketSummary,
} from '@/services/api';
import {
  Section,
  DescList,
  StatusBadge,
  LoadingState,
  ErrorState,
  EmptyState,
} from '@/components/common';
import { MEMBER_TYPE_LABEL, SNS_LABEL } from './memberCodes';

type TabKey = 'basic' | 'guardians' | 'pets' | 'tickets';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'basic', label: '기본정보' },
  { key: 'pets', label: '반려동물' },
  { key: 'tickets', label: '보유 이용권' },
  { key: 'guardians', label: '보호자 연동' },
];

const PRODUCT_TYPE_LABEL: Record<string, string> = {
  PRODUCT_TYPE_DAYCARE: '데이케어',
  PRODUCT_TYPE_HOTEL: '호텔',
  PRODUCT_TYPE_CLASS: '수업',
  PRODUCT_TYPE_GROOMING: '미용',
  PRODUCT_TYPE_KINDERGARTEN: '유치원',
};

const TICKET_STATUS_META: Record<
  string,
  { label: string; tone: 'success' | 'neutral' | 'danger' | 'warning' }
> = {
  ACTIVE: { label: '유효', tone: 'success' },
  EXHAUSTED: { label: '소진', tone: 'neutral' },
  EXPIRED: { label: '만료', tone: 'danger' },
  REVOKED: { label: '회수', tone: 'warning' },
};

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  ONLINE: '온라인',
  OFFLINE: '수동발급',
};

/**
 * 잔여 표기 — 호텔 이용권은 박수 누적 차감이 없고 1회 사용 시 전량 소진되므로
 * 잔여 박수를 보여주면 안 된다. (이용권 목록 화면과 동일 규칙)
 */
function formatRemaining(t: TicketSummary): string {
  // 회수된 이용권은 사용 이력과 무관하게 잔여 없음
  if (t.ticketStatus === 'REVOKED') {
    return '회수됨';
  }
  if (t.productType === 'PRODUCT_TYPE_HOTEL') {
    return t.usedQty > 0 ? '사용 완료' : `${t.splitCount}박 (미사용)`;
  }
  return `잔여 ${t.remainingQty} / ${t.splitCount}회`;
}

const GUARDIAN_TYPE_LABEL: Record<string, string> = {
  MAIN: '주보호자',
  SUB: '부보호자',
};

const MAPPING_STATUS_META: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
  AGREED: { label: '연동 수락', tone: 'success' },
  REQUESTED: { label: '연동 요청', tone: 'warning' },
  DENIED: { label: '연동 거절', tone: 'danger' },
};

function AlarmBadge({ value }: { value: string }) {
  const on = value === 'Y';
  return <StatusBadge tone={on ? 'success' : 'neutral'}>{on ? '허용' : '거부'}</StatusBadge>;
}

/**
 * 보유 이용권 행 — 1건당 2줄로 압축.
 *  1줄: 이용권명 + 상태 배지 + 잔여 + 만료 임박(D-n)
 *  2줄: 사업장 · 상품유형 · 펫 · 결제유형/금액 · 사용기간
 */
function TicketRow({ ticket }: { ticket: TicketSummary }) {
  const status = TICKET_STATUS_META[ticket.ticketStatus] ?? {
    label: ticket.ticketStatus,
    tone: 'neutral' as const,
  };

  // 만료 임박은 아직 쓸 수 있는 이용권에서만 의미가 있다
  const expiringSoon =
    ticket.ticketStatus === 'ACTIVE' && ticket.daysToExpire >= 0 && ticket.daysToExpire <= 14;

  const meta = [
    ticket.companyName || null,
    PRODUCT_TYPE_LABEL[ticket.productType] ?? ticket.productType,
    ticket.petName ? `${ticket.petName}` : null,
    `${PAYMENT_TYPE_LABEL[ticket.paymentType] ?? ticket.paymentType} ${Number(
      ticket.amount ?? 0,
    ).toLocaleString()}원`,
    `${(ticket.dtUsable ?? '').slice(0, 10)} ~ ${(ticket.dtExpired ?? '').slice(0, 10)}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-semibold text-gray-900">{ticket.name}</span>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
          <span className="shrink-0 text-[11px] text-gray-600">{formatRemaining(ticket)}</span>
          {expiringSoon && (
            <span className="shrink-0 rounded bg-amber-50 px-1 text-[10px] text-amber-700">
              D-{ticket.daysToExpire}
            </span>
          )}
        </div>
        <span className="shrink-0 text-[10px] text-gray-400">
          {(ticket.dtRegist ?? '').slice(0, 10)} 발급
        </span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-gray-400" title={meta}>
        {meta}
      </div>
    </div>
  );
}

/**
 * 보호자 연동 구성원 행 — 반려동물 탭과 동일하게 1인당 2줄로 압축.
 *  1줄: 이름(별칭) + 주/부보호자 + 본인 표시 + (수락 전/거절이면) 상태 배지
 *  2줄: 이메일 · 전화번호 · 반려동물 수
 */
function GuardianRow({ guardian }: { guardian: GuardianGroupMemberDto }) {
  const status = guardian.mappingStatus ? MAPPING_STATUS_META[guardian.mappingStatus] : undefined;
  const meta = [
    guardian.email || null,
    guardian.phoneNumber || null,
    `반려동물 ${guardian.petCount ?? 0}마리`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={`px-3 py-2 ${guardian.self ? 'bg-primary-50/40' : ''}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-sm font-semibold text-gray-900">
            {guardian.userName || `회원 #${guardian.userSeq}`}
          </span>
          {guardian.nickname && (
            <span className="shrink-0 text-xs text-gray-500">({guardian.nickname})</span>
          )}
          <span className="shrink-0 text-[11px] text-gray-500">
            {GUARDIAN_TYPE_LABEL[guardian.cdGuardianType ?? ''] ?? '구분 미상'}
            {guardian.self && ' · 본인'}
          </span>
          {/* 수락(AGREED)은 정상 상태라 배지를 달지 않고, 요청/거절만 눈에 띄게 표시 */}
          {status && guardian.mappingStatus !== 'AGREED' && (
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
          )}
        </div>
        <span className="shrink-0 text-[10px] text-gray-400">
          {guardian.linkedAt ? `연동 ${guardian.linkedAt.slice(0, 10)}` : ''}
        </span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-gray-400" title={meta}>
        {meta}
      </div>
    </div>
  );
}

/**
 * 반려동물 행 — 탭에 스크롤이 생기지 않도록 마리당 2줄로 압축한 레이아웃.
 *  1줄: 이름 + 기본 신상(생일/몸무게/중성화/품종/등록번호) + 수정일
 *  2줄: 접종·성향·알레르기·메모
 *  두 줄 모두 한 줄 truncate 이며, 잘린 전문은 title 툴팁(마우스 오버)으로 확인한다.
 */
function PetRow({ pet }: { pet: AdminPetInfoResponseDto }) {
  const meta = [
    pet.petBirthday || null,
    pet.petWeight ? `${pet.petWeight}Kg` : null,
    pet.neuterYn === 'Y' ? '중성화 O' : pet.neuterYn === 'N' ? '중성화 X' : null,
    pet.cdPetBreed ? `품종 ${pet.cdPetBreed}` : null,
    pet.petRegNumber ? `등록 ${pet.petRegNumber}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const notes = [
    pet.inoculation ? `접종 ${pet.inoculation}` : null,
    pet.personality ? `성향 ${pet.personality}` : null,
    pet.allergy ? `알레르기 ${pet.allergy}` : null,
    pet.memo ? `기타 ${pet.memo}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="px-3 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-sm font-semibold text-gray-900">
            {pet.petName || '이름 없음'}
          </span>
          <span className="min-w-0 truncate text-[11px] text-gray-500" title={meta}>
            {meta || '등록된 신상 정보 없음'}
          </span>
        </div>
        <span className="shrink-0 text-[10px] text-gray-400">수정 {pet.updDt || '-'}</span>
      </div>
      <div className="mt-0.5 truncate text-[11px] text-gray-400" title={notes}>
        {notes || '접종·성향·알레르기·메모 없음'}
      </div>
    </div>
  );
}

const formatDateTime = (s?: string | null) =>
  s
    ? new Date(s).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

interface MemberDetailPanelProps {
  userSeq: number;
  /** 우측 패널로 쓸 때 닫기 버튼 노출 (단독 페이지에서는 생략) */
  onClose?: () => void;
}

/**
 * 선택된 회원의 상세 — 헤더(요약) + 탭 본문
 * 컨테이너는 h-full 을 채우고 본문만 내부 스크롤한다.
 */
export function MemberDetailPanel({ userSeq, onClose }: MemberDetailPanelProps) {
  const [tab, setTab] = useState<TabKey>('basic');

  // 회원이 바뀌면 첫 탭으로 되돌린다
  useEffect(() => {
    setTab('basic');
  }, [userSeq]);

  const qc = useQueryClient();
  const toggleTestMutation = useMutation({
    mutationFn: (testYn: 'Y' | 'N') => membersApi.updateMemberTestFlag(userSeq, testYn),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['member', String(userSeq)] });
      qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
  const memberQuery = useQuery({
    queryKey: ['member', String(userSeq)],
    queryFn: () => membersApi.getMember(userSeq),
    enabled: !!userSeq,
  });

  const petsQuery = useQuery({
    queryKey: ['memberPets', String(userSeq)],
    queryFn: () => membersApi.getMemberPets(userSeq),
    enabled: !!userSeq,
  });

  const guardiansQuery = useQuery({
    queryKey: ['memberGuardians', String(userSeq)],
    queryFn: () => membersApi.getMemberGuardians(userSeq),
    enabled: !!userSeq,
  });

  const ticketsQuery = useQuery({
    queryKey: ['memberTickets', String(userSeq)],
    queryFn: () => ticketsApi.getTickets({ userSeq }),
    enabled: !!userSeq,
  });

  const member = memberQuery.data;
  const pets = petsQuery.data ?? [];
  const guardians = guardiansQuery.data?.members ?? [];

  /**
   * 이용권 정렬 — 지금 쓸 수 있는 것(유효)을 위로, 그 안에서는 만료 임박 순.
   * 소진/만료는 아래로 내리고 최근 발급 순.
   */
  const tickets = useMemo(() => {
    const list = [...(ticketsQuery.data ?? [])];
    const rank = (t: TicketSummary) => (t.ticketStatus === 'ACTIVE' ? 0 : 1);
    return list.sort((a, b) => {
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      if (a.ticketStatus === 'ACTIVE') return a.dtExpired.localeCompare(b.dtExpired);
      return b.dtRegist.localeCompare(a.dtRegist);
    });
  }, [ticketsQuery.data]);

  const activeTicketCount = tickets.filter(t => t.ticketStatus === 'ACTIVE').length;

  /**
   * 탭에 표시할 연동 상태 — 건수가 아니라 "이 회원이 지금 연동돼 있는지"를 보여준다.
   *
   * 판단 기준은 **본인 매핑 상태**가 먼저다. 주보호자의 매핑은 그룹 생성 시점부터 항상
   * AGREED 라서, 상대 상태만 보면 본인이 거절했는데도 연동중으로 보인다.
   *  · 본인 REQUESTED → 초대받고 수락 대기 = 요청중
   *  · 본인 DENIED    → 본인이 거절 = 미연동
   *  · 본인 AGREED    → 상대 중 AGREED 가 있어야 연동중, 초대만 걸려 있으면 요청중
   */
  const guardianStatus = useMemo(() => {
    if (guardiansQuery.isLoading || guardiansQuery.error) return null;

    const NONE = { label: '미연동', cls: 'bg-gray-100 text-gray-500' };
    const PENDING = { label: '요청중', cls: 'bg-amber-100 text-amber-700' };
    const LINKED = { label: '연동중', cls: 'bg-green-100 text-green-700' };

    const self = guardians.find(g => g.self);
    if (!self || self.mappingStatus === 'DENIED') return NONE;
    if (self.mappingStatus === 'REQUESTED') return PENDING;

    const others = guardians.filter(g => !g.self);
    if (others.some(g => g.mappingStatus === 'AGREED')) return LINKED;
    if (others.some(g => g.mappingStatus === 'REQUESTED')) return PENDING;
    return NONE;
  }, [guardians, guardiansQuery.isLoading, guardiansQuery.error]);

  if (memberQuery.isLoading) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <LoadingState message="회원 정보를 불러오는 중..." />
      </div>
    );
  }

  if (memberQuery.error || !member) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <ErrorState
          error={memberQuery.error ?? '회원 정보를 찾을 수 없습니다.'}
          onRetry={() => memberQuery.refetch()}
        />
      </div>
    );
  }

  const withdrawn = member.delYn === 'Y';

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border-2 border-primary-200 bg-white">
      {/* ── 헤더: 회원 요약 ── */}
      <div className="shrink-0 border-b border-gray-200 px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-bold text-gray-900">{member.userName}</h2>
              {member.nickname && (
                <span className="truncate text-sm text-gray-500">({member.nickname})</span>
              )}
              <span className="text-xs font-normal text-gray-500">SEQ {member.userSeq}</span>
              {member.testYn === 'Y' && (
                <StatusBadge tone="danger">TEST</StatusBadge>
              )}
            </div>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {member.email} · {member.phoneNumber || '연락처 없음'}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-900"
              >
                닫기 ×
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── 탭 바 ── */}
      <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-3">
        <div className="flex gap-1">
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
                {t.key === 'pets' && pets.length > 0 && (
                  <span className="ml-1 text-xs text-gray-400">{pets.length}</span>
                )}
                {t.key === 'tickets' && activeTicketCount > 0 && (
                  <span className="ml-1 text-xs text-gray-400">{activeTicketCount}</span>
                )}
                {t.key === 'guardians' && guardianStatus && (
                  <span
                    className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${guardianStatus.cls}`}
                  >
                    {guardianStatus.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 탭 본문 (내부 스크롤) ── */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4">
        {tab === 'basic' && (
          <>
            <Section title="계정 정보">
              <DescList
                cols={2}
                items={[
                  { label: '아이디(이메일)', value: member.email },
                  { label: '이름', value: member.userName },
                  { label: '닉네임', value: member.nickname || '-' },
                  { label: '휴대폰번호', value: member.phoneNumber || '-' },
                  {
                    label: '회원 유형',
                    value: MEMBER_TYPE_LABEL[member.userType] ?? member.userType ?? '-',
                  },
                  {
                    label: '가입 경로',
                    value: SNS_LABEL[member.snsType] ?? member.snsType ?? '-',
                  },
                  {
                    label: '상태',
                    value: (
                      <StatusBadge tone={withdrawn ? 'neutral' : 'success'}>
                        {withdrawn ? '탈퇴' : '활성'}
                      </StatusBadge>
                    ),
                  },
                  { label: '반려동물', value: `${pets.length}마리` },
                ]}
              />
            </Section>

            <Section title="가입 정보">
              <DescList
                cols={2}
                items={[
                  { label: '가입일', value: formatDateTime(member.regDt) },
                  { label: '최종 수정일', value: formatDateTime(member.updDt) },
                  { label: '약관동의 SEQ', value: member.logTermsSeq ?? '-' },
                  { label: '결제 고객키', value: member.customerKey || '-' },
                ]}
              />
            </Section>

            <Section title="정보 수신 설정">
              <DescList
                cols={2}
                items={[
                  { label: '알림/공지', value: <AlarmBadge value={member.alarmNoticeYn} /> },
                  { label: '마케팅정보', value: <AlarmBadge value={member.alarmMarketingYn} /> },
                  { label: 'SMS/알림톡', value: <AlarmBadge value={member.alarmSmsYn} /> },
                  { label: '이메일', value: <AlarmBadge value={member.alarmEmailYn} /> },
                ]}
              />
            </Section>

            <Section title="관리자 설정">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    테스트 계정
                    {member.testYn === 'Y' && <StatusBadge tone="danger">TEST</StatusBadge>}
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    테스트로 지정하면 운영 화면(사업자 회원검색 등)에는 테스트 계정끼리만 노출됩니다.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={toggleTestMutation.isPending}
                  onClick={() => toggleTestMutation.mutate(member.testYn === 'Y' ? 'N' : 'Y')}
                  className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                    member.testYn === 'Y'
                      ? 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {toggleTestMutation.isPending
                    ? '처리 중…'
                    : member.testYn === 'Y'
                      ? '테스트 해제'
                      : '테스트 지정'}
                </button>
              </div>
            </Section>
          </>
        )}

        {tab === 'tickets' && (
          <Section
            title="보유 이용권"
            description={
              tickets.length > 0
                ? `유효 ${activeTicketCount}건 · 소진 ${
                    tickets.filter(t => t.ticketStatus === 'EXHAUSTED').length
                  }건 · 만료 ${
                    tickets.filter(t => t.ticketStatus === 'EXPIRED').length
                  }건 · 회수 ${tickets.filter(t => t.ticketStatus === 'REVOKED').length}건`
                : undefined
            }
          >
            {ticketsQuery.isLoading ? (
              <LoadingState message="이용권 정보를 불러오는 중..." />
            ) : ticketsQuery.error ? (
              <ErrorState error={ticketsQuery.error} onRetry={() => ticketsQuery.refetch()} />
            ) : tickets.length === 0 ? (
              <EmptyState message="보유한 이용권이 없습니다." />
            ) : (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {tickets.map(t => (
                  <TicketRow key={t.purchaseTicketSeq} ticket={t} />
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === 'guardians' && (
          <Section
            title="보호자 연동 정보"
            description={
              guardiansQuery.data?.linked
                ? `그룹 #${guardiansQuery.data.guardianGroupSeq ?? '-'} · 이 회원은 ${
                    GUARDIAN_TYPE_LABEL[guardiansQuery.data.targetGuardianType ?? ''] ?? '구분 미상'
                  } · ${
                    MAPPING_STATUS_META[guardiansQuery.data.targetMappingStatus ?? '']?.label ??
                    '상태 미상'
                  }`
                : undefined
            }
          >
            {guardiansQuery.isLoading ? (
              <LoadingState message="보호자 연동 정보를 불러오는 중..." />
            ) : guardiansQuery.error ? (
              <ErrorState error={guardiansQuery.error} onRetry={() => guardiansQuery.refetch()} />
            ) : guardians.length === 0 ? (
              <EmptyState message="연동된 보호자가 없습니다." />
            ) : (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {guardians.map(g => (
                  <GuardianRow key={g.guardianGroupMappingSeq ?? g.userSeq} guardian={g} />
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === 'pets' && (
          <Section
            title="등록 반려동물 정보"
            description={pets.length > 0 ? `총 ${pets.length}마리` : undefined}
          >
            {petsQuery.isLoading ? (
              <LoadingState message="반려동물 정보를 불러오는 중..." />
            ) : petsQuery.error ? (
              <ErrorState error={petsQuery.error} onRetry={() => petsQuery.refetch()} />
            ) : pets.length === 0 ? (
              <EmptyState message="등록된 반려동물이 없습니다." />
            ) : (
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {pets.map(pet => (
                  <PetRow key={pet.petSeq} pet={pet} />
                ))}
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}
